import axiosInstance, { endpoints } from 'src/utils/axios';

import type { PerTeShelf } from 'src/types/per-te';

// hooks
import useGetData from 'src/hooks/use-get-data';
import { getStorage } from 'src/hooks/use-local-storage';
import { CONSTANT_VARIABLES } from 'src/constants/constants-variables';
// types
import {
  IProductItem,
  ICatalogShopViewFilters,
  IFetchBaseParams,
  APIListResponse,
  IListFilters,
  IProducerItem,
  ICartProductsItem,
  IAddToCart,
  IOrderPrepayment,
  ICurrentCartOrder,
  IConfirmOrder,
  ICustomerOrderItem,
  ICountries,
  IClientOrderDetails,
  IWineTypeItem,
  IClientActiveOverlayData,
  IClientDetailsData,
  IAgentAssociatedClients,
  ISelectionItem,
} from 'src/types';
import { isLoggedIn, canActAsClient } from 'src/utils/check-user-is-admin';

export function useGetProducers(params?: IListFilters<IFetchBaseParams>) {
  return useGetData<IListFilters<IFetchBaseParams>, APIListResponse<IProducerItem[]>>({
    // eslint-disable-next-line no-nested-ternary
    endpoint: canActAsClient()
      ? params?.clientId
        ? endpoints.customer.producers.root
        : null
      : endpoints.customer.producers.root,
    params,
  });
}
// /customer/products/pricing
export function useGetProducts(params?: Partial<ICatalogShopViewFilters>) {
  return useGetData<
    Partial<ICatalogShopViewFilters>,
    APIListResponse<IProductItem[]> & {
      low_price: number;
      high_price: number;
      products: IProductItem[];
    }
  >({
    endpoint: endpoints.customer.products.root,
    params,
  });
}

export function useGetSelections(clientId?: number | string | null) {
  return useGetData<{ client_id?: string }, ISelectionItem[]>({
    endpoint: endpoints.customer.selections,
    // Scoped for the same reason as the shelf: which selections a caller may
    // browse now depends on the client, so an agent must say who they are
    // acting for — and the id belongs in the cache key.
    params: clientId ? { client_id: String(clientId) } : undefined,
  });
}

/**
 * NAT-2346 — the customer's generated "Per te" shelf.
 *
 * Returns an empty `wines` array for a customer who does not get one; that is
 * the deliberate signal to hide the segment, not an error state.
 */
export function useGetPerTeShelf(clientId?: number | string | null) {
  return useGetData<{ client_id?: string }, PerTeShelf>({
    endpoint: endpoints.customer.per_te,
    // The client is part of the CACHE KEY, not just the request: without it an
    // agent switching client would be served the previous client's shelf from
    // cache before the refetch lands.
    params: clientId ? { client_id: String(clientId) } : undefined,
  });
}

export function useGetClientOrderList(params?: Partial<IFetchBaseParams>, enabled: boolean = true) {
  return useGetData<Partial<IFetchBaseParams>, APIListResponse<ICustomerOrderItem[]>>({
    endpoint: enabled ? endpoints.customer.client_orders.root : null,
    params,
  });
}

export function useGetProductsPricing() {
  return useGetData<
    {},
    {
      low_price: number;
      high_price: number;
    }
  >({
    endpoint: endpoints.customer.products.pricing,
  });
}

export function useGetWineTypes(params?: Partial<IFetchBaseParams>) {
  return useGetData<Partial<IFetchBaseParams>, IWineTypeItem[]>({
    endpoint: endpoints.customer.products.wine_types,
    params,
  });
}

export function useGetAgentAssociatedClients(params?: Partial<IFetchBaseParams>) {
  return useGetData<Partial<IFetchBaseParams>, IAgentAssociatedClients[]>({
    // NAT-2331: serves agents and admins. The endpoint returns the agent's own
    // clients for an agent and every active client for an admin, in the same
    // slim shape. Customers get a null endpoint, which SWR skips — the guard
    // must stay on the ENDPOINT, since useGetData only skips on a null one.
    endpoint: canActAsClient() ? endpoints.admin.agent_associated_clients : null,
    params,
  });
}

export function useGetGrapeTypes(params?: Partial<IFetchBaseParams>) {
  return useGetData<Partial<IFetchBaseParams>, { grape_types: { name: string; id: string }[] }>({
    endpoint: endpoints.customer.products.grape_types,
    params,
  });
}

export function useGetCountries(params?: Partial<IFetchBaseParams>) {
  return useGetData<Partial<IFetchBaseParams>, ICountries>({
    // eslint-disable-next-line no-nested-ternary
    endpoint: canActAsClient()
      ? params?.clientId
        ? endpoints.customer.producers.countries
        : null
      : endpoints.customer.producers.countries,
    params,
  });
}

export function useGetCartProducts(params?: Partial<IFetchBaseParams>) {
  // Determine endpoint based on authentication and user type
  let endpoint = null;

  if (isLoggedIn() && params?.enabled !== false) {
    if (canActAsClient()) {
      // Acting for a client (agent or admin): require the selected clientId
      endpoint = params?.clientId ? endpoints.customer.cart_products.root : null;
    } else {
      // For regular customers
      endpoint = endpoints.customer.cart_products.root;
    }
  }

  return useGetData<Partial<IFetchBaseParams>, ICartProductsItem>({
    endpoint,
    params,
  });
}

export function useGetCurrentOrder(params?: Partial<IFetchBaseParams>) {
  const isCartEmpty = params?.isCartEmpty || false;

  // Only make API call if user is logged in and cart is not empty
  let endpoint = null;
  if (isLoggedIn() && !isCartEmpty && params?.enabled !== false) {
    endpoint = endpoints.customer.client_orders.current_order;
  }

  return useGetData<Partial<IFetchBaseParams>, ICurrentCartOrder>({
    endpoint,
    params: { clientId: params?.clientId },
  });
}

export function useGetClientOrder({ order_code }: IFetchBaseParams, enabled: boolean = true) {
  return useGetData<Partial<IFetchBaseParams>, IClientOrderDetails>({
    // Null endpoint = SWR skip (same pattern as useGetClientOrderList): callers
    // that mount before an order code exists must not fire /client_orders//new.
    endpoint:
      enabled && order_code ? endpoints.customer.client_orders.details(order_code as string) : null,
  });
}
// /customer/payment-status
// implement an API for payment status updation in client order
export async function updateClientOrderPaymentFields({
  orderId,
  stripePaymentIntentId,
  clientId,
}: {
  orderId: string | null;
  stripePaymentIntentId: string | null;
  /** NAT-2497: a write names its venue. */
  clientId?: string | number | null;
}) {
  const URL = `${endpoints.customer.payment_status}`;
  await axiosInstance.put(URL, {
    orderId,
    stripePaymentIntentId,
    ...(clientId ? { clientId: String(clientId) } : {}),
  });
}

export async function addToCart(data: IAddToCart) {
  const URL = `${endpoints.customer.cart_products.root}`;
  await axiosInstance.post(URL, data);
}

export async function deleteFromCart(sku: string, clientId?: string | number | null) {
  // NAT-2497: keyed on the venue being known, not on the role — a customer's
  // delete names the venue too (a write must, once access is grant-based).
  const URL = clientId
    ? `${endpoints.customer.cart_products.root}/${sku}/${clientId}`
    : `${endpoints.customer.cart_products.root}/${sku}`;
  await axiosInstance.delete(URL);
}

export async function updateOrderPrepayment(data: IOrderPrepayment) {
  const URL = `${endpoints.customer.client_orders.current}`;
  await axiosInstance.put(URL, data);
}

export async function updateCurrentOrderAddress(data: Partial<ICurrentCartOrder>) {
  const URL = `${endpoints.customer.client_orders.current}`;
  await axiosInstance.put(URL, data);
}

export async function confirmOrder(payload: IConfirmOrder) {
  const URL = `${endpoints.customer.client_orders.current}`;
  const {
    data: { data: res },
  } = await axiosInstance.put(URL, { ...payload });
  return res;
}
// NAT-2497: every client-scoped read names the acting venue and carries it in
// its SWR key, so a switch re-fetches rather than showing the previous venue's
// minimum order and discount (spec §6). The two overlay reads are USER-keyed
// server-side and take no venue.
const venueParams = (clientId?: string | number | null) =>
  clientId ? { client_id: String(clientId) } : undefined;

export function useGetClientActiveOverlay() {
  return useGetData<Partial<IFetchBaseParams>, IClientActiveOverlayData>({
    endpoint: endpoints.customer.overlay.active,
  });
}
export function useGetClientFallbackOverlay() {
  return useGetData<Partial<IFetchBaseParams>, IClientActiveOverlayData>({
    endpoint: endpoints.customer.overlay.fallback,
  });
}
export async function MarkOverlaySeen(overlay_id: string) {
  const URL = `${endpoints.customer.overlay.mark_seen}/${overlay_id}/seen`;
  await axiosInstance.post(URL);
}
/**
 * NAT-2497 — the caller's actable venues. `[]` is a state (no active venue),
 * not an error; `undefined` means the request has not settled.
 */
export function useMyClients(enabled: boolean = true) {
  // Keyed by the signed-in person: the SWR cache outlives a sign-out in the
  // same tab, and the next person must not be seated in the previous one's
  // venues for the instant before revalidation. (The server ignores `u`.)
  const storedUser = getStorage(CONSTANT_VARIABLES.USER_STORAGE_KEY) as { id?: string } | null;
  const res = useGetData<{ u?: string }, { clients: { id: string; name: string; slug: string }[] }>(
    {
      endpoint: enabled ? endpoints.customer.my_clients : null,
      params: storedUser?.id ? { u: String(storedUser.id) } : undefined,
    },
  );
  return { ...res, data: res.data?.clients };
}

export function useGetClientDetails(clientId?: string | number | null) {
  return useGetData<{ client_id?: string }, IClientDetailsData>({
    endpoint: endpoints.customer.overlay.client_details,
    params: venueParams(clientId),
  });
}

/**
 * NAT-2497 — the one settings payload, for the venue the caller acts as. Read
 * this, keyed by venue, rather than the fields stored on the login user.
 */
export type IClientSettings = {
  clientId: string;
  clientName: string | null;
  clientSlug: string | null;
  multiplesThreshold: number | null;
  showPricesWithVat: boolean | null;
  clientDiscount: number;
  hidePrepaid: boolean | null;
  prepaid: boolean | null;
  coffeeOnly: boolean;
  paymentDays: number | string;
  discount: number;
  minimumOrderTotalEndPrice: number | null;
};

export function useGetClientSettings(clientId?: string | number | null) {
  return useGetData<{ client_id?: string }, IClientSettings | null>({
    endpoint: clientId ? endpoints.customer.client_settings : null,
    params: venueParams(clientId),
  });
}

export async function getCustomerOrderPaymentUrl(
  orderCode: string,
  clientId?: string | number | null,
) {
  const queryString = new URLSearchParams({
    orderCode: orderCode?.toString() ?? '',
    // NAT-2497: checkout binds the order to the ACTING venue server-side.
    ...(clientId ? { clientId: String(clientId) } : {}),
  }).toString();
  const URL = `${endpoints.customer.cart_products.checkout}?${queryString}`;
  const res = await axiosInstance.post(URL);
  return res;
}
